import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import Logo from "./Logo";

/**
 * Shared public-site header used by the landing page, About, and
 * How-It-Works. Owns the Home-nav behavior: clicking "Home" smoothly
 * scrolls to the top of the page when you're already on `/`, and
 * navigates to `/` (then scrolls to top) when you're elsewhere. The
 * Inter-View wordmark is the universal go-home action.
 */
const NAV_ITEMS = [
  { id: "home", label: "Home", to: "/" },
  { id: "about", label: "About", to: "/about" },
  { id: "how-it-works", label: "How It Works", to: "/how-it-works" },
  { id: "contact", label: "Contact", to: "/contact" },
];

export default function SiteHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNav = (e, item) => {
    setIsMenuOpen(false);
    // Home — on the landing page, smooth-scroll to top instead of a
    // dead-link refresh. From any other route, navigate to `/`.
    if (item.id === "home") {
      e.preventDefault();
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
        // Ensure the new page lands at the top, not at whatever scroll
        // position the previous route had.
        window.requestAnimationFrame(() =>
          window.scrollTo({ top: 0, behavior: "auto" })
        );
      }
      return;
    }
    // Contact is a hash anchor on the homepage. From other routes the
    // Link below will navigate to /#contact correctly; on the homepage
    // we let the native hash-scroll happen.
  };

  const isActive = (item) => {
    if (item.id === "home") return location.pathname === "/";
    return location.pathname === item.to;
  };

  return (
    <header
      className="bg-white/95 backdrop-blur-md border-b border-blue-100 sticky top-0 z-50"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo — universal go-home action */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              onClick={() => {
                if (location.pathname === "/") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              className="hover:opacity-90 transition-opacity inline-block"
              aria-label="Inter-View home"
              data-testid="site-header-logo"
            >
              <Logo height={34} priority className="lg:hidden" />
              <Logo height={38} priority className="hidden lg:block" />
            </Link>
          </div>

          {/* Desktop nav */}
          <nav
            className="hidden md:flex items-center space-x-1 lg:space-x-2"
            data-testid="site-header-nav"
          >
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.id}
                to={item.to}
                onClick={(e) => handleNav(e, item)}
                className={`px-3 lg:px-4 py-2 rounded-md text-[15px] font-medium transition-colors ${
                  isActive(item)
                    ? "text-blue-600"
                    : "text-gray-700 hover:text-blue-600 hover:bg-blue-50/60"
                }`}
                data-testid={`site-header-nav-${item.id}`}
                aria-current={isActive(item) ? "page" : undefined}
              >
                {item.label}
              </Link>
            ))}
            {/* Employer Experience entry — Phase 1A */}
            <Link
              to="/employers"
              className={`px-3 lg:px-4 py-2 rounded-md text-[15px] font-medium transition-colors ${
                location.pathname.startsWith("/employers")
                  ? "text-blue-600"
                  : "text-gray-700 hover:text-blue-600 hover:bg-blue-50/60"
              }`}
              data-testid="site-header-nav-for-employers"
            >
              For Employers
            </Link>
          </nav>

          {/* Auth buttons */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            <Link to="/login">
              <Button
                variant="ghost"
                size="lg"
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-[15px] px-4 lg:px-5"
              >
                Login
              </Button>
            </Link>
            <Link to="/signup">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white text-[15px] px-5 lg:px-6 shadow-md hover:shadow-lg transition-all"
              >
                Sign Up
              </Button>
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle navigation"
            data-testid="site-header-mobile-toggle"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-blue-100">
            <div className="flex flex-col space-y-4">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={(e) => handleNav(e, item)}
                  className={`font-medium px-2 py-1 transition-colors ${
                    isActive(item)
                      ? "text-blue-600"
                      : "text-gray-700 hover:text-blue-600"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/employers"
                className={`font-medium px-2 py-1 transition-colors ${
                  location.pathname.startsWith("/employers")
                    ? "text-blue-600"
                    : "text-gray-700 hover:text-blue-600"
                }`}
                data-testid="site-header-mobile-nav-for-employers"
              >
                For Employers
              </Link>
              <div className="flex flex-col space-y-2 pt-2">
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    Sign Up
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
